import { keepPreviousData } from '@tanstack/react-query';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { DrawdownService } from '@/services/drawdowns/drawdownService';
import { TransactionService } from '@/services/general-ledger/transactionService';
import { BankTransactionService } from '@/services/general-ledger/bankTransactionService';
import { BankReconciliationService } from '@/services/general-ledger/bankReconciliationService';
import type {
  LedgerQueryParams,
  TransactionQueryParams,
} from '@/types/drawdown';
import type { FundBankTransactionQueryParams } from '@/types/fund-bank-transactions';

// Query keys for general ledger
export const ledgerQueryKeys = {
  all: ['ledger'] as const,
  entries: (params?: Record<string, unknown>) =>
    [...ledgerQueryKeys.all, 'entries', params] as const,
  trialBalance: (params?: Record<string, unknown>) =>
    [...ledgerQueryKeys.all, 'trial-balance', params] as const,
  balanceSheet: (params?: Record<string, unknown>) =>
    [...ledgerQueryKeys.all, 'balance-sheet', params] as const,
  incomeStatement: (params?: Record<string, unknown>) =>
    [...ledgerQueryKeys.all, 'income-statement', params] as const,
  transactions: (fundId: string, params?: Record<string, unknown>) =>
    [...ledgerQueryKeys.all, 'transactions', fundId, params] as const,
  transactionDetail: (fundId: string, transactionId: string) =>
    [...ledgerQueryKeys.all, 'transaction', fundId, transactionId] as const,
  bankEntries: (fundId: string, params?: Record<string, unknown>) =>
    [...ledgerQueryKeys.all, 'bank-entries', fundId, params] as const,
  bankIncoming: (fundId: string) =>
    [...ledgerQueryKeys.all, 'bank-incoming', fundId] as const,
};

// Query hooks
export const useLedgerEntries = (params?: Partial<LedgerQueryParams>) => {
  const queryParams = {
    ...params,
    page: params?.page ?? 1,
    page_size: params?.page_size ?? 50,
  };

  return useApiQuery(
    ledgerQueryKeys.entries(params),
    () => DrawdownService.getLedgerEntries(queryParams),
    {
      enabled: !!params?.fund_id, // Require fund_id to enable query
      staleTime: 2 * 60 * 1000, // 2 minutes
      placeholderData: keepPreviousData,
    }
  );
};

export const useTrialBalance = (params?: {
  fund_id?: string;
  as_of_date?: string;
}) => {
  return useApiQuery(
    ledgerQueryKeys.trialBalance(params),
    () => DrawdownService.getTrialBalance(params),
    {
      enabled: !!params?.fund_id,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useBalanceSheet = (params?: {
  fund_id?: string;
  as_of_date?: string;
}) => {
  return useApiQuery(
    ledgerQueryKeys.balanceSheet(params),
    () => DrawdownService.getBalanceSheet(params),
    {
      enabled: !!params?.fund_id,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useIncomeStatement = (params?: {
  fund_id?: string;
  date_from?: string;
  date_to?: string;
}) => {
  return useApiQuery(
    ledgerQueryKeys.incomeStatement(params),
    () => DrawdownService.getIncomeStatement(params),
    {
      enabled: !!params?.fund_id,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useTransactions = (
  fundId: string | undefined,
  params?: Omit<TransactionQueryParams, 'fund_id'>
) => {
  return useApiQuery(
    ledgerQueryKeys.transactions(fundId ?? '', params),
    () => TransactionService.getTransactions(fundId!, params),
    {
      enabled: !!fundId,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

export const useTransactionDetail = (
  fundId: string | undefined,
  transactionId: string | null
) => {
  return useApiQuery(
    ledgerQueryKeys.transactionDetail(fundId ?? '', transactionId ?? ''),
    () => TransactionService.getTransactionDetail(fundId!, transactionId!),
    {
      enabled: !!fundId && !!transactionId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

export const useBankEntries = (
  fundId: string | undefined,
  params?: FundBankTransactionQueryParams
) => {
  return useApiQuery(
    ledgerQueryKeys.bankEntries(fundId ?? '', params),
    () => BankTransactionService.getBankTransactions(fundId!, params),
    {
      enabled: !!fundId,
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

// Staged "bank incoming" lines for a fund (unsettled, awaiting matching).
export const useBankIncoming = (fundId: string | undefined) => {
  return useApiQuery(
    ledgerQueryKeys.bankIncoming(fundId ?? ''),
    () => BankReconciliationService.getBankIncoming(fundId!),
    {
      enabled: !!fundId,
      staleTime: 60 * 1000, // 1 minute
    }
  );
};

// Manually match a bank-incoming line to a fund transaction (exact 1:1).
export const useMatchBankIncoming = (fundId: string | undefined) => {
  return useApiMutation(
    ({
      incomingId,
      fundTransactionId,
    }: {
      incomingId: string;
      fundTransactionId: string;
    }) =>
      BankReconciliationService.matchIncoming(
        fundId!,
        incomingId,
        fundTransactionId
      ),
    {
      successMessage: 'Bank line matched to the fund transaction',
      invalidateKeys: () => [
        ledgerQueryKeys.bankIncoming(fundId ?? ''),
        [...ledgerQueryKeys.all, 'bank-entries', fundId ?? ''],
      ],
    }
  );
};

// Mutation hooks
export const useCreateAdjustmentEntry = () => {
  return useApiMutation(
    ({
      fundId,
      description,
      adjustments,
      options,
    }: {
      fundId: string;
      description: string;
      adjustments: {
        account_code: string;
        debit_amount?: number;
        credit_amount?: number;
        investor_id?: string;
      }[];
      options?: {
        notes?: string;
        reference?: string;
        document_date?: string;
      };
    }) =>
      DrawdownService.createAdjustmentEntry(
        fundId,
        description,
        adjustments,
        options
      ),
    {
      invalidateQueries: [queryKeys.drawdowns.all, ledgerQueryKeys.all],
      successMessage: 'Adjustment entry created successfully',
    }
  );
};
